import {
  createMaterialTopTabNavigator,
  MaterialTopTabBarProps,
} from '@react-navigation/material-top-tabs';
import * as Sentry from '@sentry/react-native';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';

import { Alert } from '@components/Alert/index.web';
import CustomTabBar from '@components/CustomTabBar/CustomTabBar';
import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import { Box } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { TabNavigatorSwipeSpacer } from '@components/shared/TabNavigatorSwipeSpacer';
import {
  ChatDrawerScreen,
  useWebDrawer,
} from '@components/Web/Drawer/WebDrawerContext';
import { PopupFilesFilterButtonWeb } from '@components/Web/PopupFilesFilterButton.web';
import { PopupGlobalSearchProjectsFilterButtonWeb } from '@components/Web/PopupGlobalSearchProjectsFilterButton.web';
import { PopupGlobalSearchProjectsSortButtonWeb } from '@components/Web/PopupGlobalSearchProjectsSortButton.web';
import { PopupTaskButtonWebV3 } from '@components/Web/PopupTaskButtonV3.web';
import { PopupTaskSortButtonV3 } from '@components/Web/PopupTaskSortButtonV3.web';
import {
  Document,
  Project,
  SearchableResult,
  SearchQueryVariables,
  Task,
  TaskPriority,
  TaskSortOption,
  useCreateChatMutation,
  useListUsersLazyQuery,
  User,
  useSearchLazyQuery,
} from '@graphql/generated';
import useActiveChat from '@hooks/useActiveChat';
import { useGetDocumentsByClientIdsQuery } from '@hooks/useGetDocumentsByClientIdsQuery';
import useGlobalSearch, { SearchLocation } from '@hooks/useGlobalSearch';
import useMe from '@hooks/useMe';
import { usePreviewDocuments } from '@hooks/usePreviewDocuments';
import useTabFocus from '@hooks/useTabFocus';
import { useToggleCompleteTask } from '@hooks/useToggleCompleteTask';
import { ThemedNavigationContainer } from '@navigation/ThemedNavigationContainer.web';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { Search, SearchResultType } from '@root/types';
import { ImagesPreview } from '@screens/Chats/ImagesPreview.web';
import ContactsSearch from '@screens/Global/ContactsSearch.web';
import FilesSearch from '@screens/Global/FilesSearch.web';
import {
  buildSearchQueryVariables,
  DEFAULT_PROJECT_SORT_STATUS,
  ProjectSortStatus,
  resolveSearchTab,
  SearchTabName,
  toTaskSearchSortOption,
} from '@screens/Global/globalSearchWebUtils';
import ProjectsSearch from '@screens/Global/ProjectsSearch.web';
import TasksSearch from '@screens/Global/TasksSearch.web';
import { MIN_SEARCH_CHARS, SEARCH_DEBOUNCE_MS } from '@src/constants/constants';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { openInNewTab } from '@utils/fileUtils';
import {
  isContactSearchResult,
  isDocumentSearchResult,
  isProjectSearchResult,
  isTaskSearchResult,
} from '@utils/typeGuards';

const GLOBAL_SEARCH_ALLOWED_SORTS = Object.values(TaskSortOption).filter(
  (sort) => !!toTaskSearchSortOption(sort as TaskSortOption)
) as TaskSortOption[];

const EMPTY_TAB_COUNTS: Record<SearchTabName, number> = {
  Projects: 0,
  Tasks: 0,
  Files: 0,
  Contacts: 0,
};

const INITIAL_SEARCH_RESULTS: SearchResultType = {
  messages: [],
  tasks: [],
  documents: [],
  projects: [],
  contacts: [],
};

const TabNavigationContainer = ({ children }: { children: ReactNode }) => (
  <ThemedNavigationContainer independent>{children}</ThemedNavigationContainer>
);

const Tab = createMaterialTopTabNavigator();

const GlobalSearch: React.FC<{
  visible?: boolean;
  doAfterPressItem?: () => void;
}> = ({ visible = true, doAfterPressItem }) => {
  // this param visible is just for fixing the bug, search box lose focus after entering the first character,
  // this bug is because Tab.Navigator appear make input-box lose focus, so I make it always there -- April
  // https://tasktag.atlassian.net/browse/TA-2995

  const PAGESIZE = 30;
  const { t } = useTranslation('models');

  const [projectsSortStatus, setProjectsSortStatus] =
    useState<ProjectSortStatus>(DEFAULT_PROJECT_SORT_STATUS);
  const [projectsSortVisible, setProjectsSortVisible] = useState(false);
  const [taskFilterProjects, setTaskFilterProjects] = useState<Project[]>([]);
  const [taskFilterAssignees, setTaskFilterAssignees] = useState<ContactType[]>(
    []
  );
  const [taskFilterPriorities, setTaskFilterPriorities] = useState<
    TaskPriority[]
  >([]);
  const [taskFilterDuedate, setTaskFilterDuedate] = useState<DateRange>({});
  const [taskFilterDueDateStrList, setTaskFilterDueDateStrList] = useState<
    string[]
  >([]);
  const [taskSortList, setTaskSortList] = useState<TaskSortOption[]>([]);
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [tabCounts, setTabCounts] =
    useState<Record<SearchTabName, number>>(EMPTY_TAB_COUNTS);

  const { checkClientIds } = useGetDocumentsByClientIdsQuery();

  const offsetPopButton: Partial<any> = {
    position: 'right',
    offset: [0, 4],
  };

  const appNavigation = useAppNavigation();
  const { tabFocus, setTabFocus } = useTabFocus();
  // const [navigationSearchTabs, setNavigationSearchTabs] = useState();
  const navigationSearchTabsRef = useRef<any>(null);
  const { search, setSearch, location, setLocation, setIsSearchFocus } =
    useGlobalSearch();

  const [swiping, setSwiping] = useState(false);
  const { toggleCompleteTask } = useToggleCompleteTask();
  const { me } = useMe();

  const { setActiveMessageId, setIsOpenChat } = useActiveChat();
  const { chatExpanded, chatListOpen, setChatListOpen, setShouldChatListOpen } =
    useWebDrawer();

  const { setPreviewDocuments, setActiveIndex } = usePreviewDocuments();
  const [isImageCarouselVisible, setImageCarouselVisible] = useState(false);
  const [isMyUploads, setIsMyUploads] = useState(false);
  const [filesFilter, setFilesFilter] = useState<string[]>([]);
  const [selectedProjectOwners, setSelectedProjectOwners] = useState<
    ContactType[]
  >([]);
  const [selectedProjectMembers, setSelectedProjectMembers] = useState<
    ContactType[]
  >([]);
  const [selectedProjectTeamIds, setSelectedProjectTeamIds] = useState<
    string[]
  >([]);
  const [projectsFilterVisible, setProjectsFilterVisible] = useState(false);
  const [projectsFilterOpenSection, setProjectsFilterOpenSection] = useState<
    'owner' | 'team' | 'member' | null
  >('owner');
  const [listUsers, { data: taskTagUsers }] = useListUsersLazyQuery();
  const isBelowThreshold = search.trim().length < MIN_SEARCH_CHARS;

  // Refs for stable debounce - store current values to avoid recreating debounced function
  const searchRef = useRef(search);
  const filesFilterRef = useRef(filesFilter);
  const isMyUploadsRef = useRef(isMyUploads);
  const tabFocusRef = useRef(tabFocus);
  const selectedProjectOwnersRef = useRef(selectedProjectOwners);
  const selectedProjectMembersRef = useRef(selectedProjectMembers);
  const selectedProjectTeamIdsRef = useRef(selectedProjectTeamIds);
  const taskFilterProjectsRef = useRef(taskFilterProjects);
  const taskFilterAssigneesRef = useRef(taskFilterAssignees);
  const taskFilterPrioritiesRef = useRef(taskFilterPriorities);
  const taskFilterDuedateRef = useRef(taskFilterDuedate);
  const taskFilterDueDateStrListRef = useRef(taskFilterDueDateStrList);
  const taskSortListRef = useRef(taskSortList);
  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Request ID for stale response guard
  const latestRequestIdRef = useRef(0);
  // Tab associated with the most recently-started request.
  // This must be set for both normal searches and load-more requests, and read
  // when applying `setTabCounts` so badge updates follow the request that
  // produced the response instead of the tab that happens to be focused later.
  const latestRequestTabRef = useRef<SearchTabName | ''>('');
  // Tabs already searched for the current (term, filters) combo — switching
  // back to one of these tabs reuses the cached result instead of
  // re-querying the server. Cleared whenever the term or any filter changes
  // (TA-10216).
  const fetchedTabsRef = useRef<Set<SearchTabName>>(new Set());
  // True while a load-more request is in flight; badge count must not change during pagination
  const isLoadMoreRef = useRef(false);

  // Update refs when values change. searchRef holds the trimmed term since
  // it feeds query variables directly — untrimmed whitespace shouldn't reach
  // the backend even if isBelowThreshold's own trimming already gates when
  // a search fires at all.
  useEffect(() => {
    searchRef.current = search.trim();
  }, [search]);

  useEffect(() => {
    filesFilterRef.current = filesFilter;
  }, [filesFilter]);

  useEffect(() => {
    isMyUploadsRef.current = isMyUploads;
  }, [isMyUploads]);

  useEffect(() => {
    tabFocusRef.current = tabFocus;
  }, [tabFocus]);

  useEffect(() => {
    selectedProjectOwnersRef.current = selectedProjectOwners;
  }, [selectedProjectOwners]);

  useEffect(() => {
    selectedProjectMembersRef.current = selectedProjectMembers;
  }, [selectedProjectMembers]);

  useEffect(() => {
    selectedProjectTeamIdsRef.current = selectedProjectTeamIds;
  }, [selectedProjectTeamIds]);

  useEffect(() => {
    taskFilterProjectsRef.current = taskFilterProjects;
  }, [taskFilterProjects]);

  useEffect(() => {
    taskFilterAssigneesRef.current = taskFilterAssignees;
  }, [taskFilterAssignees]);

  useEffect(() => {
    taskFilterPrioritiesRef.current = taskFilterPriorities;
  }, [taskFilterPriorities]);

  useEffect(() => {
    taskFilterDuedateRef.current = taskFilterDuedate;
  }, [taskFilterDuedate]);

  useEffect(() => {
    taskFilterDueDateStrListRef.current = taskFilterDueDateStrList;
  }, [taskFilterDueDateStrList]);

  useEffect(() => {
    taskSortListRef.current = taskSortList;
  }, [taskSortList]);

  const updateLength = (
    category: 'Contacts' | 'Projects' | 'Tasks' | 'Files',
    resultArray: Array<{ total: number }>,
    setLength: (length: number) => void
  ) => {
    if (tabFocus === category) {
      if (resultArray.length > 0 && resultArray.length < resultArray[0].total) {
        setLength(resultArray.length);
      } else {
        setLength(0);
      }
    }
  };
  const [createChat] = useCreateChatMutation();
  const openChat = (item: User) => {
    const chatName = [item.firstName, item.lastName]
      .filter((item) => !item)
      .join(' ');
    createChat({
      variables: {
        attributes: {
          name: chatName ?? item.name,
          userIds: [item.id],
        },
      },
      onCompleted: (data) => {
        const { createChat: createChatData } = data;
        if (!chatExpanded && chatListOpen) {
          setShouldChatListOpen(true);
          setChatListOpen(false);
        }
        setIsOpenChat(true);
        setActiveMessageId?.(undefined);
        appNavigation.navigateToChatDrawer({
          screen: ChatDrawerScreen.details,
          activeChat: createChatData,
        });
      },
      onError: (error) => Alert.alert(t('shared:error'), error.message),
    });
  };

  const [globalSearch, { data, loading, error: searchError }] =
    useSearchLazyQuery();
  // Aborts from AbortController are expected mid-typing — not real backend failures.
  const hasRealSearchError =
    !!searchError && searchError.networkError?.name !== 'AbortError';
  // Memoized on `data` (Apollo's own stable reference) rather than
  // recomputed inline — the `|| []` fallback otherwise creates a NEW array
  // on every render while `data` is still undefined (before the very first
  // response of a cold session ever arrives), which kept re-triggering the
  // results-repopulate effect below (keyed on this value) and immediately
  // undoing `setIsSearchPending(true)` on almost every render — the search
  // never got to show a loading state on a cold mount (TA-10278).
  const searchData = useMemo(
    () => (data?.search as Search<SearchableResult>[]) || [],
    [data]
  );

  const [searchResults, setSearchResults] = useState(INITIAL_SEARCH_RESULTS);
  // Bumped whenever a search request actually completes, independent of
  // whether Apollo returns a new `data` object reference. Apollo reuses the
  // same reference for a deep-equal repeat result, so the repopulate effect
  // below can't rely on that reference alone to know a search settled
  // (TA-10179).
  const [searchGeneration, setSearchGeneration] = useState(0);
  const [contactLength, setContactLength] = useState(0);
  const [projectLength, setProjectLength] = useState(0);
  const [taskLength, setTaskLength] = useState(0);
  const [filesLength, setFilesLength] = useState(0);

  const getLoadMore = (results: SearchResultType) => {
    updateLength('Contacts', results.messages, setContactLength);
    updateLength('Projects', results.projects, setProjectLength);
    updateLength('Tasks', results.tasks, setTaskLength);
    updateLength('Files', results.documents, setFilesLength);
  };

  const listUsersToContactType = convertUsersToContacts(
    (taskTagUsers?.listUsers as User[]) || []
  ).filter((u) => !searchResults.contacts.some((c) => c.record.id === u.id));

  const tabIsLoading = (tab: SearchLocation): boolean => {
    return (
      (location.includes(tab) && loading) ||
      (swiping && !location.includes(tab))
    );
  };

  const buildVariablesForTab = useCallback(
    (
      tab: SearchTabName,
      overrides?: Partial<Pick<SearchQueryVariables, 'from' | 'size' | 'term'>>
    ): SearchQueryVariables =>
      buildSearchQueryVariables({
        term: overrides?.term ?? searchRef.current,
        tab,
        size: overrides?.size ?? PAGESIZE,
        from: overrides?.from ?? 0,
        projectsSortStatus,
        filesFilter: filesFilterRef.current,
        isMyUploads: isMyUploadsRef.current,
        meId: me?.id ?? undefined,
        projectOwnerIds: selectedProjectOwnersRef.current.map(
          (owner) => owner.id
        ),
        projectUserIds: selectedProjectMembersRef.current.map(
          (member) => member.id
        ),
        teamIds: selectedProjectTeamIdsRef.current,
        taskUserIds: taskFilterAssigneesRef.current.map(
          (assignee) => assignee.id
        ),
        projectIds: taskFilterProjectsRef.current.map((project) => project.id),
        taskPriority: taskFilterPrioritiesRef.current,
        taskFilterDuedate: taskFilterDuedateRef.current,
        taskFilterDueDateStrList: taskFilterDueDateStrListRef.current,
        taskSortList: taskSortListRef.current,
      }),
    [PAGESIZE, me?.id, projectsSortStatus]
  );

  const globalSearchCall = useCallback(
    (fromOffset?: number) => {
      isLoadMoreRef.current = false;
      const currentTabFocus = tabFocusRef.current;

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Increment request ID and capture the tab for this request. Narrowed
      // via resolveSearchTab (not a raw `as SearchTabName` cast) since
      // tabFocus is a global, app-wide context that can also hold non-search
      // values like 'Chats'/'Teams' — the ref and the query variables below
      // must always agree on which tab the request is actually for.
      const requestId = ++latestRequestIdRef.current;
      const searchTab = resolveSearchTab(currentTabFocus);
      latestRequestTabRef.current = searchTab;

      const from = fromOffset ?? 0;

      return globalSearch({
        variables: buildVariablesForTab(searchTab, {
          from,
        }),
        context: {
          fetchOptions: {
            signal: abortController.signal,
          },
        },
      })
        .then((result) => {
          if (requestId === latestRequestIdRef.current) {
            setSearchGeneration((g) => g + 1);
          }
          return result;
        })
        .catch((error) => {
          // Ignore abort errors - they're expected when cancelling requests
          if (error?.name === 'AbortError') {
            return;
          }
          // Only log non-abort errors if this is still the latest request
          if (requestId === latestRequestIdRef.current) {
            setIsSearchPending(false);
            Sentry.captureException(error);
          }
        });
    },
    [buildVariablesForTab, globalSearch]
  );

  // Legacy globalSearchCall for load more (uses current state values for offset)
  const globalSearchCallWithOffset = useCallback(() => {
    isLoadMoreRef.current = true;
    const currentTabFocus = tabFocusRef.current;

    let from = projectLength;
    if (currentTabFocus === 'Contacts') {
      from = contactLength;
    } else if (currentTabFocus === 'Tasks') {
      from = taskLength;
    } else if (currentTabFocus === 'Files') {
      from = filesLength;
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Share the same request-id guard as globalSearchCall so a load-more
    // response that resolves after being superseded (abort doesn't help if
    // the response already arrived) can't force a repopulate against
    // whatever the current tab/term has since moved on to. Also set
    // latestRequestTabRef here — it must be kept current for load-more
    // requests too, not just normal searches. Narrowed via resolveSearchTab,
    // same as globalSearchCall, since tabFocus can hold non-search values.
    const requestId = ++latestRequestIdRef.current;
    const searchTab = resolveSearchTab(currentTabFocus);
    latestRequestTabRef.current = searchTab;

    return globalSearch({
      variables: buildVariablesForTab(searchTab, {
        from,
        term: search.trim(),
      }),
      context: {
        fetchOptions: {
          signal: abortController.signal,
        },
      },
    })
      .then((result) => {
        if (requestId === latestRequestIdRef.current) {
          setSearchGeneration((g) => g + 1);
        }
        return result;
      })
      .catch((error) => {
        // Ignore abort errors
        if (error?.name === 'AbortError') {
          return;
        }
      });
  }, [
    globalSearch,
    search,
    tabFocus,
    projectLength,
    contactLength,
    taskLength,
    filesLength,
    buildVariablesForTab,
  ]);

  // Stable debounced function - created once and reads from refs
  const debouncedGlobalSearch = useMemo(
    () =>
      debounce(() => {
        globalSearchCall();
      }, SEARCH_DEBOUNCE_MS),
    [globalSearchCall]
  );

  // Debounced listUsers call
  const debouncedListUsers = useMemo(
    () =>
      debounce((term: string) => {
        if (term) {
          listUsers({ variables: { term } });
        }
      }, SEARCH_DEBOUNCE_MS),
    [listUsers]
  );

  // Effect to trigger debounced search when search term or filters change.
  // No backend search fires below MIN_SEARCH_CHARS (TA-10091) — isSearchPending
  // already covers the full debounce+request window for the loading spinner,
  // so it stays false rather than flashing true for a search that never fires.
  useEffect(() => {
    setSearchResults(INITIAL_SEARCH_RESULTS);
    setTabCounts(EMPTY_TAB_COUNTS);
    fetchedTabsRef.current = new Set();

    setFilesLength(0);
    setContactLength(0);
    setProjectLength(0);
    setTaskLength(0);

    // Abort any request already in flight from before this term/filter
    // change, regardless of whether the new term is above or below
    // threshold — otherwise a stale response can land after the reset above
    // and repopulate searchResults/tabCounts (and mark fetchedTabsRef) for
    // the previous term/filters (TA-10216).
    abortControllerRef.current?.abort();

    if (isBelowThreshold) {
      setIsSearchPending(false);
      debouncedGlobalSearch.cancel();
      debouncedListUsers.cancel();
      return;
    }

    setIsSearchPending(true);

    // Use debounced listUsers call instead of immediate call. Trimmed to
    // match the threshold check above — otherwise a term like '  abc  '
    // passes the gate but queries with the untrimmed whitespace.
    debouncedListUsers(search.trim());

    // Trigger debounced search
    debouncedGlobalSearch();

    return () => {
      debouncedGlobalSearch.cancel();
      debouncedListUsers.cancel();
    };
  }, [
    search,
    projectsSortStatus,
    filesFilter,
    isMyUploads,
    selectedProjectOwners,
    selectedProjectMembers,
    selectedProjectTeamIds,
    taskFilterProjects,
    taskFilterAssignees,
    taskFilterPriorities,
    taskFilterDuedate,
    taskFilterDueDateStrList,
    taskSortList,
    debouncedGlobalSearch,
    debouncedListUsers,
  ]);

  // Reset all filters when search is cleared
  useEffect(() => {
    if (!search) {
      setTaskFilterProjects([]);
      setTaskFilterAssignees([]);
      setTaskFilterPriorities([]);
      setTaskFilterDuedate({});
      setTaskFilterDueDateStrList([]);
      setTaskSortList([]);
      setProjectsSortStatus(DEFAULT_PROJECT_SORT_STATUS);
      setFilesFilter([]);
      setIsMyUploads(false);
      setSelectedProjectOwners([]);
      setSelectedProjectMembers([]);
      setSelectedProjectTeamIds([]);
      setProjectsFilterVisible(false);
      setProjectsSortVisible(false);
    }
  }, [search]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      debouncedGlobalSearch.cancel();
      debouncedListUsers.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedGlobalSearch, debouncedListUsers]);

  useEffect(() => {
    if (tabFocus === '') {
      setTabFocus('Projects');
    } else if (
      tabFocus &&
      navigationSearchTabsRef.current &&
      tabFocus !== 'Projects'
    ) {
      navigationSearchTabsRef.current.jumpTo(tabFocus);
    }
  }, [tabFocus]);

  // Effect to trigger a search when the active tab changes, reusing the
  // cached result instead of re-querying if this tab was already searched
  // for the current term/filters (TA-10216).
  // Cancel any pending debounced calls to avoid race conditions
  useEffect(() => {
    // Cancel pending debounced search to avoid race conditions
    debouncedGlobalSearch.cancel();
    if (isBelowThreshold) return;
    // '' is a transient value the effect above immediately corrects to
    // 'Projects' — skip firing here so that correction doesn't cause two
    // back-to-back requests for Projects before fetchedTabsRef has had a
    // chance to record the first one.
    if (!tabFocus) return;
    if (fetchedTabsRef.current.has(resolveSearchTab(tabFocus))) {
      return;
    }
    // Unlike the debounced typing-trigger effect, this path fires
    // globalSearchCall() directly — without this, the skeleton's
    // visibility for a tab switch fell back entirely to `tabIsLoading()`
    // (Apollo's raw `loading` flag), which clears one render before the
    // results-repopulate effect actually finishes updating
    // searchResults/totalCount, flashing the empty state in between
    // (TA-10278 QA comment 58385).
    setIsSearchPending(true);
    globalSearchCall();
  }, [tabFocus, isBelowThreshold, debouncedGlobalSearch, globalSearchCall]);

  useEffect(() => {
    // Guard against a race between this effect's own triggers and Apollo's
    // reactive `data`/`loading` state actually committing: `searchGeneration`
    // is bumped from the search-completion PROMISE's `.then()` callback
    // (needed so a repeated, deep-equal search still re-triggers this effect
    // — TA-10179, since Apollo reuses the same `data` reference for it), but
    // that promise resolving isn't strictly guaranteed to land in the same
    // render as Apollo's own `data`/`loading` hook values updating. If this
    // effect ran while `loading` (Apollo's own flag) is still true, `data` —
    // and therefore `searchData`/`totalCount` — hasn't actually caught up
    // yet; finalizing here would prematurely flip `isSearchPending` false
    // while the list is still genuinely empty, flashing the no-results empty
    // state before the real response lands (TA-10278). `searchData` changing
    // again once `data` truly updates re-fires this effect for real.
    //
    // A second, distinct way `loading` can flip false without a real
    // response: an in-flight request getting aborted (term/filter change or
    // tab switch superseding it — see abortControllerRef usage above) surfaces
    // as an AbortError, which also flips Apollo's `loading` true -> false.
    // The effect that triggered the abort has already set
    // `isSearchPending(true)` for the *new* debounce window by this point;
    // finalizing here against stale/empty `searchData` would immediately
    // undo that and flash the same premature "no results" state (TA-10180).
    // The request that superseded the aborted one re-fires this effect once
    // its own response lands.
    if (loading || searchError?.networkError?.name === 'AbortError') return;

    const results = searchData.reduce<SearchResultType>((previous, result) => {
      if (
        previous.projects.some(
          (existingResult) =>
            result.record.__typename === 'Project' &&
            existingResult.record.id === result.record.id
        )
      )
        return previous;
      if (isProjectSearchResult(result))
        return { ...previous, projects: [...previous.projects, result] };
      const exists =
        result.record.__typename === 'Task' &&
        previous.tasks.some(
          (existingResult) => existingResult.record.id === result.record.id
        );

      if (exists) {
        return {
          ...previous,
          tasks: previous.tasks.map((existingResult) =>
            existingResult.record.id === result.record.id
              ? result
              : existingResult
          ),
        };
      }
      if (isTaskSearchResult(result))
        return { ...previous, tasks: [...previous.tasks, result] };
      if (
        previous.documents.some(
          (existingResult) =>
            result.record.__typename === 'Document' &&
            existingResult.record.id === result.record.id
        )
      )
        return previous;
      if (isDocumentSearchResult(result))
        return { ...previous, documents: [...previous.documents, result] };
      if (
        previous.contacts.some(
          (existingResult) =>
            result.record.__typename === 'User' &&
            existingResult.record.id === result.record.id
        )
      )
        return previous;
      if (isContactSearchResult(result))
        return { ...previous, contacts: [...previous.contacts, result] };

      return previous;
    }, searchResults);
    if (results.documents.length > 0) {
      checkClientIds(
        results.documents.map((newItem) => newItem.record.clientId)
      );
    }
    setSearchResults(results);
    setIsSearchPending(false);
    getLoadMore(results);

    // Attribute the count (and cache the fetch) to the tab that produced
    // this response — latestRequestTabRef.current — not tabFocusRef.current,
    // so a late-arriving response can't overwrite a different tab's badge
    // after the user has switched away (TA-10216). This replaces the old
    // refreshTabCounts() which fired 4 parallel queries for all tabs on every
    // keystroke; counts now stay consistent with what is displayed,
    // including empty-result responses which should reset the badge to 0.
    const respondingTab = latestRequestTabRef.current;
    if (respondingTab && !isLoadMoreRef.current) {
      const total = searchData[0]?.total ?? 0;
      setTabCounts((prev) => ({ ...prev, [respondingTab]: total }));
      fetchedTabsRef.current.add(respondingTab);
    }
  }, [searchData, searchGeneration, loading, searchError]);

  useEffect(() => {
    if (!searchError) return;
    const isAbort = searchError.networkError?.name === 'AbortError';
    if (!isAbort && !isLoadMoreRef.current) {
      setSearchResults(INITIAL_SEARCH_RESULTS);
      setIsSearchPending(false);
      setTabCounts(EMPTY_TAB_COUNTS);
    }
  }, [searchError]);

  const renderTabBar = (props: MaterialTopTabBarProps) => {
    return (
      <Box px='l'>
        <Box
          flexDirection='row'
          alignItems='center'
          borderBottomWidth={1}
          borderBottomColor='grey02'>
          <CustomTabBar
            {...props}
            initialRouteName={tabFocus}
            spacing={theme.spacing.l}
            tabWidth={108}
            contentContainerStyle={styles.contentContainerStyle}
          />
        </Box>
        <Box marginBottom='xs' />
      </Box>
    );
  };

  // Server already filters by projectOwnerIds, projectUserIds, teamIds — no client-side re-filter needed.
  const filteredProjectResults = searchResults.projects;

  const filteredTaskResults = useMemo(() => {
    let results = searchResults.tasks as Search<Task>[];

    if (taskFilterProjects.length > 0) {
      const projectIds = taskFilterProjects.map((p) => p.id);
      results = results.filter((item) =>
        projectIds.includes((item.record as Task).projectId ?? '')
      );
    }

    if (taskFilterAssignees.length > 0) {
      const assigneeIds = taskFilterAssignees.map((a) => a.id);
      results = results.filter((item) =>
        (item.record as Task).userIds?.some((id) => assigneeIds.includes(id))
      );
    }

    if (taskFilterPriorities.length > 0) {
      results = results.filter((item) =>
        taskFilterPriorities.includes(
          (item.record as Task).priority as TaskPriority
        )
      );
    }

    if (taskFilterDueDateStrList.length > 0) {
      const today = dayjs().startOf('day');
      results = results.filter((item) => {
        const due = (item.record as Task).dueDate;
        if (!due) return false;
        const d = dayjs(due);
        return taskFilterDueDateStrList.some((opt) => {
          if (opt === 'Today') return d.isSame(today, 'day');
          if (opt === 'Tomorrow') return d.isSame(today.add(1, 'day'), 'day');
          if (opt === 'Next 3 days')
            return (
              !d.isBefore(today) && d.isBefore(today.add(3, 'day').endOf('day'))
            );
          return false;
        });
      });
    }

    if (taskFilterDuedate.start || taskFilterDuedate.end) {
      results = results.filter((item) => {
        const due = (item.record as Task).dueDate;
        if (!due) return false;
        const d = dayjs(due);
        if (
          taskFilterDuedate.start &&
          d.isBefore(taskFilterDuedate.start.startOf('day'))
        )
          return false;

        return !(
          taskFilterDuedate.end && d.isAfter(taskFilterDuedate.end.endOf('day'))
        );
      });
    }

    return results;
  }, [
    searchResults.tasks,
    taskFilterProjects,
    taskFilterAssignees,
    taskFilterPriorities,
    taskFilterDueDateStrList,
    taskFilterDuedate,
  ]);

  const sortedTaskResults = useMemo(() => {
    if (taskSortList.length === 0) return filteredTaskResults;
    const sort = taskSortList[0];
    const priorityRank: Record<string, number> = {
      [TaskPriority.Low]: 1,
      [TaskPriority.Medium]: 2,
      [TaskPriority.High]: 3,
    };
    return [...filteredTaskResults].sort((a, b) => {
      const ta = a.record as Task;
      const tb = b.record as Task;
      switch (sort) {
        case TaskSortOption.DueDateAsc:
          return (ta.dueDate ?? '').localeCompare(tb.dueDate ?? '');
        case TaskSortOption.DueDateDesc:
          return (tb.dueDate ?? '').localeCompare(ta.dueDate ?? '');
        case TaskSortOption.PriorityAsc:
          return (
            (priorityRank[ta.priority ?? ''] ?? 0) -
            (priorityRank[tb.priority ?? ''] ?? 0)
          );
        case TaskSortOption.PriorityDesc:
          return (
            (priorityRank[tb.priority ?? ''] ?? 0) -
            (priorityRank[ta.priority ?? ''] ?? 0)
          );
        default:
          return 0;
      }
    });
  }, [filteredTaskResults, taskSortList]);

  const projectsTabBadgeCount = tabCounts.Projects;
  const tasksTabBadgeCount = tabCounts.Tasks;
  const filesTabBadgeCount = tabCounts.Files;
  const contactsTabBadgeCount = tabCounts.Contacts;

  const renderLoadMoreComponent = (): JSX.Element => {
    if (
      (tabFocus === 'Tasks' && taskLength > 0) ||
      (tabFocus === 'Projects' && projectLength > 0) ||
      (tabFocus === 'Files' && filesLength > 0) ||
      (tabFocus === 'Contacts' && contactLength > 0)
    ) {
      if (loading)
        return (
          <Box alignItems='center' minHeight={40} p='s' minWidth={100}>
            <ActivityIndicatorLoading />
          </Box>
        );
    }
    return <Box mb='s' />;
  };

  const getGlobalSearch = () => {
    return (
      <Box
        flex={1}
        style={{ opacity: search ? 1 : 0 }}
        pointerEvents={search ? 'auto' : 'none'}>
        <TabNavigationContainer>
          <Tab.Navigator
            screenOptions={{
              swipeEnabled: false,
            }}
            sceneContainerStyle={styles.sceneContainerStyle}
            tabBar={renderTabBar}
            initialRouteName={tabFocus}
            backBehavior='none'
            screenListeners={{
              state: (e) => {
                const tabLocation =
                  e?.data?.state.routeNames[e.data.state.index];
                if (!tabLocation) return;
                setLocation(tabLocation);
                setTabFocus(tabLocation);
              },
              swipeStart: () => setSwiping(true),
              swipeEnd: () => setSwiping(false),
            }}>
            {!visible && (
              <Tab.Screen
                name='Projects'
                options={{
                  tabBarLabel: 'Projects',
                  tabBarCount: projectsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Projects'),
                }}
                initialParams={{
                  tabBarLabel: 'Projects',
                  tabBarCount: projectsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Projects'),
                }}
                children={() => {
                  return <Box />;
                }}
              />
            )}
            {visible && (
              <Tab.Screen
                name='Projects'
                options={{
                  tabBarLabel: 'Projects',
                  tabBarCount: projectsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Projects'),
                }}
                initialParams={{
                  tabBarLabel: 'Projects',
                  tabBarCount: projectsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Projects'),
                }}
                children={({ navigation }) => {
                  if (!navigationSearchTabsRef.current) {
                    navigationSearchTabsRef.current = navigation;
                  }
                  return (
                    <SearchTabContainer>
                      <ProjectsSearch
                        filterPart={
                          <Box flexDirection='row' alignItems='center' gap='xs'>
                            <PopupGlobalSearchProjectsFilterButtonWeb
                              title={t('shared:filter')}
                              boxProps={offsetPopButton}
                              selectedOwners={selectedProjectOwners}
                              selectedMembers={selectedProjectMembers}
                              selectedTeamIds={selectedProjectTeamIds}
                              onSelectOwner={setSelectedProjectOwners}
                              onSelectMember={setSelectedProjectMembers}
                              onSelectTeamIds={setSelectedProjectTeamIds}
                              visible={projectsFilterVisible}
                              onVisibleChange={setProjectsFilterVisible}
                              openSection={projectsFilterOpenSection}
                              onOpenSectionChange={setProjectsFilterOpenSection}
                            />
                            <PopupGlobalSearchProjectsSortButtonWeb
                              title={t('shared:status')}
                              boxProps={offsetPopButton}
                              selectedStatus={projectsSortStatus}
                              onSelectStatus={setProjectsSortStatus}
                              visible={projectsSortVisible}
                              onVisibleChange={setProjectsSortVisible}
                            />
                          </Box>
                        }
                        sectionFooter={renderLoadMoreComponent()}
                        onEndReached={() => {
                          if (
                            tabFocus === 'Projects' &&
                            projectLength > 0 &&
                            (data?.search.length ?? 0) > 0 &&
                            projectLength < (data?.search![0].total ?? 0)
                          ) {
                            globalSearchCallWithOffset();
                          }
                        }}
                        search={search}
                        data={filteredProjectResults}
                        totalCount={data?.search?.[0]?.total ?? 0}
                        loading={
                          isSearchPending ||
                          (projectLength === 0 && tabIsLoading('Projects'))
                        }
                        hasError={hasRealSearchError && !isSearchPending}
                        onPress={(project: Project) => {
                          setIsSearchFocus(false);
                          doAfterPressItem?.();
                          setSearch('');
                          setTabFocus('Projects');
                          appNavigation.navigateToProject(project);
                        }}
                      />
                    </SearchTabContainer>
                  );
                }}
              />
            )}
            {visible && (
              <Tab.Screen
                name='Tasks'
                options={{
                  tabBarLabel: 'Tasks',
                  tabBarCount: tasksTabBadgeCount,
                  tabBarCountLoading: isSearchPending || tabIsLoading('Tasks'),
                }}
                initialParams={{
                  tabBarLabel: 'Tasks',
                  tabBarCount: tasksTabBadgeCount,
                  tabBarCountLoading: isSearchPending || tabIsLoading('Tasks'),
                }}
                children={({ navigation }) => {
                  if (!navigationSearchTabsRef.current) {
                    navigationSearchTabsRef.current = navigation;
                  }

                  return (
                    <SearchTabContainer>
                      <TasksSearch
                        sectionFooter={renderLoadMoreComponent()}
                        onEndReached={() => {
                          if (
                            tabFocus === 'Tasks' &&
                            taskLength > 0 &&
                            (data?.search.length ?? 0) > 0 &&
                            taskLength < (data?.search![0].total ?? 0)
                          ) {
                            globalSearchCallWithOffset();
                          }
                        }}
                        filterPart={
                          <Box flexDirection='row' alignItems='center' gap='xs'>
                            <PopupTaskButtonWebV3
                              title={t('shared:filter')}
                              iconName='AlignCenterDesc'
                              hideSearch
                              boxProps={offsetPopButton}
                              selectedProjects={taskFilterProjects}
                              onSelectProjects={setTaskFilterProjects}
                              selectedAssignee={taskFilterAssignees}
                              onSelectAssignee={setTaskFilterAssignees}
                              selectedPriorities={taskFilterPriorities}
                              onSelectPriority={setTaskFilterPriorities}
                              selectedDuedate={taskFilterDuedate}
                              onSelectDuedate={setTaskFilterDuedate}
                              selectedDueDateStrList={taskFilterDueDateStrList}
                              onSelectedDueDateStrList={
                                setTaskFilterDueDateStrList
                              }
                            />
                            <PopupTaskSortButtonV3
                              boxProps={offsetPopButton}
                              selectedSorts={taskSortList}
                              onSelectedSorts={setTaskSortList}
                              allowedSortIds={GLOBAL_SEARCH_ALLOWED_SORTS}
                            />
                          </Box>
                        }
                        search={search}
                        data={sortedTaskResults}
                        toggleCompleteTask={(task) => {
                          toggleCompleteTask(task, true);
                        }}
                        loading={
                          isSearchPending ||
                          (taskLength === 0 && tabIsLoading('Tasks'))
                        }
                        hasError={hasRealSearchError && !isSearchPending}
                        onPress={(task: Task) => {
                          setIsSearchFocus(false);
                          doAfterPressItem?.();
                          setSearch('');
                          setTabFocus('Tasks');
                          appNavigation.navigateToTask(task);
                        }}
                      />
                    </SearchTabContainer>
                  );
                }}
              />
            )}
            {visible && (
              <Tab.Screen
                name='Files'
                options={{
                  tabBarLabel: 'Files',
                  tabBarCount: filesTabBadgeCount,
                  tabBarCountLoading: isSearchPending || tabIsLoading('Files'),
                }}
                initialParams={{
                  tabBarLabel: 'Files',
                  tabBarCount: filesTabBadgeCount,
                  tabBarCountLoading: isSearchPending || tabIsLoading('Files'),
                }}
                children={({ navigation }) => {
                  if (!navigationSearchTabsRef.current) {
                    navigationSearchTabsRef.current = navigation;
                  }
                  return (
                    <SearchTabContainer>
                      <FilesSearch
                        filterPart={
                          <PopupFilesFilterButtonWeb
                            title={t('shared:filter')}
                            isMyUploads={isMyUploads}
                            setIsMyUploads={setIsMyUploads}
                            filesFilter={filesFilter}
                            setFilesFilter={setFilesFilter}
                            boxProps={offsetPopButton}
                          />
                        }
                        sectionFooter={renderLoadMoreComponent()}
                        onEndReached={() => {
                          if (
                            tabFocus === 'Files' &&
                            filesLength > 0 &&
                            (data?.search.length ?? 0) > 0 &&
                            filesLength < (data?.search![0].total ?? 0)
                          ) {
                            globalSearchCallWithOffset();
                          }
                        }}
                        search={search}
                        data={searchResults.documents}
                        loading={
                          isSearchPending ||
                          (filesLength === 0 && tabIsLoading('Files'))
                        }
                        hasError={hasRealSearchError && !isSearchPending}
                        onPress={(document: Document) => {
                          if (
                            document.contentType?.startsWith('image/') ||
                            document.isImage
                          ) {
                            const documents = searchResults.documents.filter(
                              (item) =>
                                item.record?.contentType?.startsWith(
                                  'image/'
                                ) || item.record?.isImage
                            );
                            setPreviewDocuments(
                              documents.map((item) => item?.record)
                            );
                            setActiveIndex(
                              documents.findIndex(
                                (i) => i.record.id === document.id
                              )
                            );
                            return setImageCarouselVisible(true);
                          } else {
                            return openInNewTab(document);
                          }
                        }}
                      />
                    </SearchTabContainer>
                  );
                }}
              />
            )}
            {visible && (
              <Tab.Screen
                name='Contacts'
                options={{
                  tabBarLabel: 'Contacts',
                  tabBarCount: contactsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Contacts'),
                }}
                initialParams={{
                  tabBarLabel: 'Contacts',
                  tabBarCount: contactsTabBadgeCount,
                  tabBarCountLoading:
                    isSearchPending || tabIsLoading('Contacts'),
                }}
                children={({ navigation }) => {
                  if (!navigationSearchTabsRef.current) {
                    navigationSearchTabsRef.current = navigation;
                  }
                  return (
                    <SearchTabContainer>
                      <ContactsSearch
                        sectionFooter={renderLoadMoreComponent()}
                        onEndReached={() => {
                          if (
                            tabFocus === 'Contacts' &&
                            contactLength > 0 &&
                            (data?.search.length ?? 0) > 0 &&
                            contactLength < (data?.search![0].total ?? 0)
                          ) {
                            globalSearchCallWithOffset();
                          }
                        }}
                        search={search}
                        contacts={searchResults.contacts}
                        taskTagUsers={
                          !isBelowThreshold ? listUsersToContactType : undefined
                        }
                        loading={
                          isSearchPending ||
                          (contactLength === 0 && tabIsLoading('Contacts'))
                        }
                        hasError={hasRealSearchError && !isSearchPending}
                        onPress={(contact: User) => {
                          setIsSearchFocus(false);
                          doAfterPressItem?.();
                          setSearch('');
                          setTabFocus('Contacts');
                          appNavigation.navigateToContact(contact);
                        }}
                        onMessagePress={(contact: User) => {
                          setIsSearchFocus(false);
                          doAfterPressItem?.();
                          setSearch('');
                          setTabFocus('Contacts');
                          openChat(contact);
                        }}
                      />
                    </SearchTabContainer>
                  );
                }}
              />
            )}
          </Tab.Navigator>
        </TabNavigationContainer>
        <TabNavigatorSwipeSpacer />
        {isImageCarouselVisible && (
          <ImagesPreview
            onDismiss={() => setImageCarouselVisible(false)}
            isGlobalSearch
          />
        )}
      </Box>
    );
  };

  return getGlobalSearch();
};

const SearchTabContainer = ({ children }: { children: ReactNode }) => (
  <Box flex={1}>{children}</Box>
);

const styles = StyleSheet.create({
  contentContainerStyle: {
    width: '100%',
    justifyContent: 'flex-start',
  },
  sceneContainerStyle: {
    backgroundColor: 'transparent',
  },
});

export default GlobalSearch;
